import {
  useEffect,
  useId,
  useMemo,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import {
  AUTOMATION_ACTION_LIMIT,
  AUTOMATION_ACTIVE_BUTTON_LIMIT,
  AUTOMATION_CHAIN_DEPTH_LIMIT,
  AUTOMATION_EMAIL_RECIPIENT_LIMIT,
  AUTOMATION_ACTION_KINDS,
  AUTOMATION_RULE_TRIGGER_KINDS,
  AUTOMATION_TEXT_VARIABLES,
  type AutomationCondition,
  type AutomationConditionBody,
  createAutomationCommandBodySchema,
  type AutomationActionKind,
  type AutomationCommand,
  type AutomationCommandKind,
  type AutomationHttpMethod,
  type AutomationRuleTriggerKind,
  type AutomationUnsupportedTriggerReasonCode,
  unsupportedAutomationTriggerReason,
  type BoardMember,
  type CreateAutomationCommandBody,
  type Label,
} from '@molt/shared';
import { Button, Notice, TextField } from '@/components';
import {
  AutomationScheduleFields,
  type AutomationScheduleDraft,
} from '@/components/automation/AutomationScheduleFields';
import type { BoardFaceList } from '@molt/shared';
import { cn } from '@/lib/cn';
import {
  WEB_INTERFACE_MESSAGES,
  interfaceMessage,
  useInterfaceLanguage,
} from '@/lib/interface-language';

export interface AutomationEditorProps {
  boardId: string;
  currentUserId?: string | null | undefined;
  lists: readonly BoardFaceList[];
  labels: readonly Label[];
  members: readonly BoardMember[];
  commandKind: AutomationCommandKind;
  command?: AutomationCommand | null | undefined;
  busy?: boolean | undefined;
  error?: string | undefined;
  activeButtonLimitNotice?: string | undefined;
  onSubmit: (body: CreateAutomationCommandBody) => void;
  onCancel: () => void;
}

/**
 * ⚠ The screen half of the command-kind twin. Exported because
 * `BoardAutomationSection` draws these five words in five places — the kind tab
 * strip, the `<h4>` above the list, the activity row line, the administrator
 * row line and the definition dialog.
 */
export const AUTOMATION_COMMAND_LABEL_KEYS: Record<AutomationCommandKind, string> = {
  rule: 'automationAuthoring.commandKind.rule',
  card_button: 'automationAuthoring.commandKind.card_button',
  board_button: 'automationAuthoring.commandKind.board_button',
  calendar_command: 'automationAuthoring.commandKind.calendar_command',
  due_date_command: 'automationAuthoring.commandKind.due_date_command',
};

/**
 * ⚠ THE CAP SENTENCE THE EDITOR NO LONGER TAKES ON TRUST. Until the automation
 * log contract landed, the active-button cap existed only as private literals
 * that had already drifted in NAME with nothing red, and this editor drew a
 * numeral its caller had resolved from one of them. `AUTOMATION_ACTIVE_BUTTON_LIMIT`
 * is now exported from `@molt/shared`, so the authoring surface composes the cap
 * sentence ITSELF from the one authority, and states the cap on a button kind
 * even when no caller hands it a notice.
 *
 * `activeButtonLimitNotice` survives and still WINS when it is passed: a caller
 * that has the command list knows whether THIS author is already at the cap and
 * can say something the editor cannot derive. The prop is the more specific
 * message, not the only one.
 *
 * Keyed by the WIRE spelling, `null` on the three kinds that are not buttons,
 * so a sixth `AutomationCommandKind` is a compile error rather than a silently
 * missing warning.
 */
const ACTIVE_BUTTON_LIMIT_KEYS: Record<AutomationCommandKind, string | null> = {
  rule: null,
  card_button: 'automationAuthoring.commands.buttonLimitCard',
  board_button: 'automationAuthoring.commands.buttonLimitBoard',
  calendar_command: null,
  due_date_command: null,
};

/**
 * ⚠ The screen half of the trigger twin. Keyed by the WIRE spelling from
 * `AUTOMATION_RULE_TRIGGER_KINDS`, so the pin can check the arms against the
 * enum mechanically. Reached as `t(RULE_TRIGGER_LABEL_KEYS[kind])` — invisible
 * to the `t('…')` anchor by construction.
 */
export const RULE_TRIGGER_LABEL_KEYS: Record<AutomationRuleTriggerKind, string> = {
  card_added_to_list: 'automationAuthoring.editor.triggerOption.card_added_to_list',
  card_moved_into_list: 'automationAuthoring.editor.triggerOption.card_moved_into_list',
  card_moved_out_of_list:
    'automationAuthoring.editor.triggerOption.card_moved_out_of_list',
  card_archived: 'automationAuthoring.editor.triggerOption.card_archived',
  card_unarchived: 'automationAuthoring.editor.triggerOption.card_unarchived',
  label_added: 'automationAuthoring.editor.triggerOption.label_added',
  label_removed: 'automationAuthoring.editor.triggerOption.label_removed',
  member_joined_card: 'automationAuthoring.editor.triggerOption.member_joined_card',
  member_left_card: 'automationAuthoring.editor.triggerOption.member_left_card',
  due_date_added: 'automationAuthoring.editor.triggerOption.due_date_added',
  due_date_changed: 'automationAuthoring.editor.triggerOption.due_date_changed',
  due_date_removed: 'automationAuthoring.editor.triggerOption.due_date_removed',
  due_date_marked_complete:
    'automationAuthoring.editor.triggerOption.due_date_marked_complete',
  checklist_added: 'automationAuthoring.editor.triggerOption.checklist_added',
  checklist_completed: 'automationAuthoring.editor.triggerOption.checklist_completed',
  checklist_item_checked:
    'automationAuthoring.editor.triggerOption.checklist_item_checked',
  checklist_item_unchecked:
    'automationAuthoring.editor.triggerOption.checklist_item_unchecked',
  comment_posted: 'automationAuthoring.editor.triggerOption.comment_posted',
  comment_matching_text_posted:
    'automationAuthoring.editor.triggerOption.comment_matching_text_posted',
  attachment_added: 'automationAuthoring.editor.triggerOption.attachment_added',
  card_voted_on: 'automationAuthoring.editor.triggerOption.card_voted_on',
  custom_field_value_changed:
    'automationAuthoring.editor.triggerOption.custom_field_value_changed',
  card_name_matches: 'automationAuthoring.editor.triggerOption.card_name_matches',
  list_created: 'automationAuthoring.editor.triggerOption.list_created',
  list_renamed: 'automationAuthoring.editor.triggerOption.list_renamed',
  list_archived: 'automationAuthoring.editor.triggerOption.list_archived',
};

/**
 * `BE-68.11` — THE SCREEN HALF OF THE UNSUPPORTED-TRIGGER FACT. Keyed by the
 * SHARED reason code from `AUTOMATION_UNSUPPORTED_TRIGGER_KINDS`, so the
 * sentence a board admin reads is the B80 catalogue's and never the snake_case
 * wire word. Reached as `t(UNSUPPORTED_TRIGGER_REASON_KEYS[code])` — invisible
 * to the `t('…')` anchor by construction, exactly like
 * `RULE_TRIGGER_LABEL_KEYS` above, so its two keys are held in the pin's
 * `VARIABLE_KEYS`.
 *
 * ⚠ The `Record` is TOTAL over the shared code union and that is load-bearing:
 * a third code declared in `packages/shared` fails this file to COMPILE until a
 * sentence exists for it, rather than rendering an undefined key at run time.
 *
 * ⚠ There is deliberately NO English here. This table is not a twin of a wire
 * half — nothing in it can reach `plainText`.
 */
const UNSUPPORTED_TRIGGER_REASON_KEYS: Record<
  AutomationUnsupportedTriggerReasonCode,
  string
> = {
  no_activity_event:
    'automationAuthoring.editor.trigger.unsupportedReason.no_activity_event',
  no_completion_aggregate:
    'automationAuthoring.editor.trigger.unsupportedReason.no_completion_aggregate',
};

/**
 * ⚠ The screen half of the action twin, read from FOUR chrome positions — the
 * `<option>`, the numbered action-row heading, the `{action} text` field
 * label, and the administrator definition dialog's action list, which stopped
 * printing `actionKind.replaceAll('_', ' ')` when this table arrived.
 */
export const ACTION_LABEL_KEYS: Record<AutomationActionKind, string> = {
  move_card: 'automationAuthoring.editor.actionOption.move_card',
  copy_card: 'automationAuthoring.editor.actionOption.copy_card',
  mirror_card: 'automationAuthoring.editor.actionOption.mirror_card',
  archive_card: 'automationAuthoring.editor.actionOption.archive_card',
  unarchive_card: 'automationAuthoring.editor.actionOption.unarchive_card',
  delete_card: 'automationAuthoring.editor.actionOption.delete_card',
  rename_card: 'automationAuthoring.editor.actionOption.rename_card',
  set_due_date: 'automationAuthoring.editor.actionOption.set_due_date',
  move_due_date: 'automationAuthoring.editor.actionOption.move_due_date',
  remove_due_date: 'automationAuthoring.editor.actionOption.remove_due_date',
  mark_due_date_complete:
    'automationAuthoring.editor.actionOption.mark_due_date_complete',
  add_label: 'automationAuthoring.editor.actionOption.add_label',
  remove_label: 'automationAuthoring.editor.actionOption.remove_label',
  add_member: 'automationAuthoring.editor.actionOption.add_member',
  remove_member: 'automationAuthoring.editor.actionOption.remove_member',
  post_comment: 'automationAuthoring.editor.actionOption.post_comment',
  create_card: 'automationAuthoring.editor.actionOption.create_card',
  create_list: 'automationAuthoring.editor.actionOption.create_list',
  rename_list: 'automationAuthoring.editor.actionOption.rename_list',
  sort_list: 'automationAuthoring.editor.actionOption.sort_list',
  move_all_cards_in_list:
    'automationAuthoring.editor.actionOption.move_all_cards_in_list',
  add_checklist: 'automationAuthoring.editor.actionOption.add_checklist',
  remove_checklist: 'automationAuthoring.editor.actionOption.remove_checklist',
  check_checklist_item: 'automationAuthoring.editor.actionOption.check_checklist_item',
  uncheck_checklist_item:
    'automationAuthoring.editor.actionOption.uncheck_checklist_item',
  set_custom_field_value:
    'automationAuthoring.editor.actionOption.set_custom_field_value',
  send_email: 'automationAuthoring.editor.actionOption.send_email',
  http_request: 'automationAuthoring.editor.actionOption.http_request',
};

const RUNTIME_SUPPORTED_ACTIONS = new Set<AutomationActionKind>([
  'move_card',
  'archive_card',
  'rename_card',
  'set_due_date',
  'remove_due_date',
  'mark_due_date_complete',
  'add_label',
  'remove_label',
  'post_comment',
  'create_card',
  'http_request',
]);

const MASKED_SECRET_HEADER_VALUE = '********';

const SECRET_HEADER_NAMES = new Set([
  'authorization',
  'proxy-authorization',
  'x-api-key',
  'api-key',
  'x-auth-token',
  'cookie',
  'set-cookie',
]);

function localTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function defaultSchedule(): AutomationScheduleDraft {
  return {
    frequency: 'weekly',
    daysOfWeek: ['monday'],
    dayOfMonth: null,
    monthDay: null,
    time: '09:00',
    timezone: localTimezone(),
  };
}

function scheduleDraftFromCommand(
  command: AutomationCommand | null | undefined,
): AutomationScheduleDraft {
  if (command?.schedule === null || command?.schedule === undefined)
    return defaultSchedule();
  return {
    frequency: command.schedule.frequency,
    daysOfWeek: [...command.schedule.daysOfWeek],
    dayOfMonth: command.schedule.dayOfMonth,
    monthDay: command.schedule.monthDay,
    time: command.schedule.time.slice(0, 5),
    timezone: command.schedule.timezone,
  };
}

interface DraftAction {
  readonly key: string;
  readonly actionKind: AutomationActionKind;
  readonly targetListId: string;
  readonly targetLabelId: string;
  readonly text: string;
  readonly days: number;
  readonly httpMethod: AutomationHttpMethod;
  readonly httpUrl: string;
  readonly httpHeaders: string;
  readonly httpBody: string;
  readonly emailTo: string;
  readonly emailCc: string;
  readonly emailSubject: string;
  readonly emailBody: string;
}

type ConditionMode =
  | 'actor_me'
  | 'actor_not_me'
  | 'actor_specific_member'
  | 'label'
  | 'without_label'
  | 'assigned_to_me'
  | 'assigned_to_member'
  | 'due_date'
  | 'list_name'
  | 'card_text'
  | 'comment_text';

interface DraftCondition {
  readonly key: string;
  readonly mode: ConditionMode;
  readonly labelId: string;
  readonly memberUserId: string;
  readonly text: string;
}

function firstListId(lists: readonly BoardFaceList[]): string {
  return lists[0]?.id ?? '';
}

function firstLabelId(labels: readonly Label[]): string {
  return labels[0]?.id ?? '';
}

function firstMemberId(members: readonly BoardMember[]): string {
  return members[0]?.id ?? '';
}

function initialTriggerListId(
  commandKind: AutomationCommandKind,
  command: AutomationCommand | null | undefined,
  lists: readonly BoardFaceList[],
): string {
  if (
    command?.trigger?.targetListId !== null &&
    command?.trigger?.targetListId !== undefined
  ) {
    return command.trigger.targetListId;
  }
  return commandKind === 'due_date_command' ? '' : firstListId(lists);
}

/**
 * ⚠ THE FOURTH TWIN, ONE VALUE WIDE. `labelName` keeps its literal fallback
 * because the same fallback also reaches the composed sentence, which is
 * posted as `plainText`. The `<option>` render calls `labelOptionName` instead,
 * so a label with no name reads a routed `Unnamed label` on screen while the
 * stored sentence stays English.
 */
function labelName(label: Label): string {
  const name = label.name?.trim() ?? '';
  return name.length === 0 ? 'Unnamed label' : name;
}

function labelOptionName(label: Label, unnamed: string): string {
  const name = label.name?.trim() ?? '';
  return name.length === 0 ? unnamed : name;
}

function isAutomationHttpMethod(value: unknown): value is AutomationHttpMethod {
  return value === 'GET' || value === 'POST' || value === 'PUT';
}

function headerLinesFromParameters(headers: unknown): string {
  if (headers === null || typeof headers !== 'object' || Array.isArray(headers)) {
    return '';
  }
  return Object.entries(headers as Record<string, unknown>)
    .map(
      ([name, value]) => `${name}: ${typeof value === 'string' ? value : String(value)}`,
    )
    .join('\n');
}

function recipientLinesFromParameters(recipients: unknown): string {
  if (!Array.isArray(recipients)) return '';
  return recipients
    .filter((recipient): recipient is string => typeof recipient === 'string')
    .join('\n');
}

function parseEmailRecipients(value: string): string[] {
  return value
    .split(/[,\n]/)
    .map((recipient) => recipient.trim())
    .filter((recipient) => recipient.length > 0);
}

function hasMalformedEmailRecipient(recipients: readonly string[]): boolean {
  return recipients.some((recipient) => {
    if (recipient.length > 320) return true;
    const at = recipient.indexOf('@');
    return (
      at <= 0 ||
      at !== recipient.lastIndexOf('@') ||
      at === recipient.length - 1 ||
      recipient.slice(at + 1).indexOf('.') <= 0
    );
  });
}

/**
 * ⚠ RETURNS A KEY, NOT A SENTENCE. A module function sits above the component
 * and cannot call `t`; a resolved sentence stored in state would also keep
 * announcing the previous language after a switch (defect-ledger rule 4). The
 * caller resolves.
 */
function parseHeaderLines(value: string): {
  readonly headers: Record<string, string>;
  readonly error: string | null;
  readonly maskedSecretHeaderName: string | null;
} {
  const headers: Record<string, string> = {};
  let maskedSecretHeaderName: string | null = null;
  const lines = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  for (const line of lines) {
    const separator = line.indexOf(':');
    if (separator <= 0) {
      return {
        headers,
        error: 'automationAuthoring.editor.http.headerFormatError',
        maskedSecretHeaderName,
      };
    }
    const name = line.slice(0, separator).trim();
    const headerValue = line.slice(separator + 1).trim();
    if (name.length === 0) {
      return {
        headers,
        error: 'automationAuthoring.editor.http.headerFormatError',
        maskedSecretHeaderName,
      };
    }
    headers[name] = headerValue;
    if (
      SECRET_HEADER_NAMES.has(name.toLowerCase()) &&
      headerValue === MASKED_SECRET_HEADER_VALUE
    ) {
      maskedSecretHeaderName = name;
    }
  }

  return { headers, error: null, maskedSecretHeaderName };
}

function actionFromCommandAction(
  action: AutomationCommand['actions'][number],
  index: number,
): DraftAction {
  const params = action.parameters as Record<string, unknown>;
  return {
    key: `${action.id}-${index}`,
    actionKind: action.actionKind,
    targetListId: action.targetListId ?? '',
    targetLabelId: action.targetLabelId ?? '',
    text:
      typeof params['body'] === 'string'
        ? params['body']
        : typeof params['title'] === 'string'
          ? params['title']
          : '',
    days: typeof params['daysFromNow'] === 'number' ? params['daysFromNow'] : 3,
    httpMethod: isAutomationHttpMethod(params['method']) ? params['method'] : 'POST',
    httpUrl: typeof params['url'] === 'string' ? params['url'] : '',
    httpHeaders: headerLinesFromParameters(params['headers']),
    httpBody: typeof params['body'] === 'string' ? params['body'] : '',
    emailTo: recipientLinesFromParameters(params['to']),
    emailCc: recipientLinesFromParameters(params['cc']),
    emailSubject: typeof params['subject'] === 'string' ? params['subject'] : '',
    emailBody: typeof params['body'] === 'string' ? params['body'] : '',
  };
}

function newDraftCondition(
  mode: ConditionMode,
  labels: readonly Label[],
  members: readonly BoardMember[],
): DraftCondition {
  return {
    key: `${mode}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    mode,
    labelId: firstLabelId(labels),
    memberUserId: firstMemberId(members),
    text: mode === 'comment_text' ? '/done' : mode === 'list_name' ? 'Done' : 'urgent',
  };
}

function conditionFromCommandCondition(
  condition: AutomationCondition,
  index: number,
  labels: readonly Label[],
  members: readonly BoardMember[],
  actingUserId: string,
): DraftCondition | null {
  const raw = condition as {
    readonly memberUserId?: string | null;
    readonly labelId?: string | null;
    readonly textValue?: string | null;
  };
  if (
    condition.conditionKind === 'actor' &&
    condition.operator === 'is' &&
    (('subject' in condition && condition.subject === 'acting_member') ||
      (raw.memberUserId === null && raw.textValue === 'me'))
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'actor_me',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'actor' &&
    condition.operator === 'is_not' &&
    (('subject' in condition && condition.subject === 'acting_member') ||
      (raw.memberUserId === null && raw.textValue === 'me'))
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'actor_not_me',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'actor' &&
    condition.operator === 'is' &&
    raw.memberUserId !== undefined &&
    raw.memberUserId !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'actor_specific_member',
      labelId: firstLabelId(labels),
      memberUserId: raw.memberUserId,
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'card_state' &&
    (condition.operator === 'has' || condition.operator === 'has_not') &&
    (('field' in condition && condition.field === 'label') || raw.labelId !== null) &&
    raw.labelId !== undefined &&
    raw.labelId !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: condition.operator === 'has_not' ? 'without_label' : 'label',
      labelId: raw.labelId,
      memberUserId: firstMemberId(members),
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'card_state' &&
    condition.operator === 'has' &&
    (('field' in condition && condition.field === 'member') ||
      raw.memberUserId !== null) &&
    raw.memberUserId !== undefined &&
    raw.memberUserId !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: raw.memberUserId === actingUserId ? 'assigned_to_me' : 'assigned_to_member',
      labelId: firstLabelId(labels),
      memberUserId: raw.memberUserId,
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'card_state' &&
    condition.operator === 'has' &&
    ((('field' in condition && condition.field === 'due_date') ||
      raw.textValue === 'due_date') ??
      false)
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'due_date',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: 'urgent',
    };
  }
  if (
    condition.conditionKind === 'card_state' &&
    condition.operator === 'contains' &&
    ((('field' in condition && condition.field === 'list_name') ||
      raw.textValue?.startsWith('list_name:')) ??
      false) &&
    raw.textValue !== undefined &&
    raw.textValue !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'list_name',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: raw.textValue.replace(/^list_name:/, ''),
    };
  }
  if (
    condition.conditionKind === 'card_state' &&
    condition.operator === 'contains' &&
    ((('field' in condition && condition.field === 'card_name') ||
      raw.textValue?.startsWith('card_name:')) ??
      false) &&
    raw.textValue !== undefined &&
    raw.textValue !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'card_text',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: raw.textValue.replace(/^card_name:/, ''),
    };
  }
  if (
    condition.conditionKind === 'text_match' &&
    condition.operator === 'contains' &&
    ((('source' in condition && condition.source === 'comment_body') ||
      raw.textValue?.startsWith('comment:')) ??
      false) &&
    raw.textValue !== undefined &&
    raw.textValue !== null
  ) {
    return {
      key: `${condition.id}-${index}`,
      mode: 'comment_text',
      labelId: firstLabelId(labels),
      memberUserId: firstMemberId(members),
      text: raw.textValue.replace(/^comment:/, ''),
    };
  }
  return null;
}

function newDraftAction(
  actionKind: AutomationActionKind,
  lists: readonly BoardFaceList[],
  labels: readonly Label[],
): DraftAction {
  return {
    key: `${actionKind}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    actionKind,
    targetListId: firstListId(lists),
    targetLabelId: firstLabelId(labels),
    text:
      actionKind === 'post_comment'
        ? 'Moved by {username} on {date}'
        : actionKind === 'rename_card'
          ? 'Renamed by automation'
          : actionKind === 'create_card' || actionKind === 'create_list'
            ? 'Weekly review {date}'
            : '',
    days: 3,
    httpMethod: 'POST',
    httpUrl: '',
    httpHeaders: '',
    httpBody: '{"card":"{cardlink}"}',
    emailTo: '',
    emailCc: '',
    emailSubject: '',
    emailBody: 'Card {cardname}: {cardlink}',
  };
}

function triggerNeedsList(triggerKind: AutomationRuleTriggerKind): boolean {
  return [
    'card_added_to_list',
    'card_moved_into_list',
    'card_moved_out_of_list',
  ].includes(triggerKind);
}

function triggerNeedsText(triggerKind: AutomationRuleTriggerKind): boolean {
  return (
    triggerKind === 'comment_matching_text_posted' || triggerKind === 'card_name_matches'
  );
}

type AutomationPreviewTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

function sourcePreviewMessage(
  key: string,
  values?: Record<string, string | number>,
  count?: number,
): string {
  const message = WEB_INTERFACE_MESSAGES[key];
  if (message === undefined) {
    throw new Error(`automation preview message is missing: ${key}`);
  }
  return interfaceMessage(message, values, count);
}

function conditionText(
  t: AutomationPreviewTranslate,
  conditionMode: ConditionMode,
  labels: readonly Label[],
  members: readonly BoardMember[],
  labelId: string,
  memberUserId: string,
  text: string,
): string {
  if (conditionMode === 'actor_me')
    return t('automationAuthoring.editor.preview.condition.actorMe');
  if (conditionMode === 'actor_not_me')
    return t('automationAuthoring.editor.preview.condition.actorNotMe');
  if (conditionMode === 'actor_specific_member') {
    const member = members.find((item) => item.id === memberUserId);
    return t('automationAuthoring.editor.preview.condition.actorMember', {
      member:
        member === undefined
          ? t('automationAuthoring.editor.preview.fallback.member')
          : member.displayName,
    });
  }
  if (conditionMode === 'label' || conditionMode === 'without_label') {
    const label = labels.find((item) => item.id === labelId);
    const labelText =
      label === undefined
        ? t('automationAuthoring.editor.preview.fallback.label')
        : labelName(label);
    return conditionMode === 'without_label'
      ? t('automationAuthoring.editor.preview.condition.withoutLabel', {
          label: labelText,
        })
      : t('automationAuthoring.editor.preview.condition.withLabel', {
          label: labelText,
        });
  }
  if (conditionMode === 'assigned_to_me')
    return t('automationAuthoring.editor.preview.condition.assignedToMe');
  if (conditionMode === 'assigned_to_member') {
    const member = members.find((item) => item.id === memberUserId);
    return t('automationAuthoring.editor.preview.condition.assignedToMember', {
      member:
        member === undefined
          ? t('automationAuthoring.editor.preview.fallback.member')
          : member.displayName,
    });
  }
  if (conditionMode === 'due_date')
    return t('automationAuthoring.editor.preview.condition.dueDate');
  if (conditionMode === 'list_name')
    return t('automationAuthoring.editor.preview.condition.listName', { text });
  if (conditionMode === 'card_text')
    return t('automationAuthoring.editor.preview.condition.cardText', { text });
  if (conditionMode === 'comment_text')
    return t('automationAuthoring.editor.preview.condition.commentText', { text });
  return '';
}

function buildCondition(
  condition: DraftCondition,
  actingUserId: string,
): AutomationConditionBody {
  if (condition.mode === 'actor_me') {
    return {
      conditionKind: 'actor',
      operator: 'is',
      subject: 'acting_member',
    };
  }
  if (condition.mode === 'actor_not_me') {
    return {
      conditionKind: 'actor',
      operator: 'is_not',
      subject: 'acting_member',
    };
  }
  if (condition.mode === 'actor_specific_member') {
    return {
      conditionKind: 'actor',
      operator: 'is',
      subject: 'specific_member',
      memberUserId: condition.memberUserId,
    };
  }
  if (condition.mode === 'label' || condition.mode === 'without_label') {
    return {
      conditionKind: 'card_state',
      field: 'label',
      operator: condition.mode === 'without_label' ? 'has_not' : 'has',
      labelId: condition.labelId,
    };
  }
  if (condition.mode === 'assigned_to_me') {
    return {
      conditionKind: 'card_state',
      field: 'member',
      operator: 'has',
      memberUserId: actingUserId,
    };
  }
  if (condition.mode === 'assigned_to_member') {
    return {
      conditionKind: 'card_state',
      field: 'member',
      operator: 'has',
      memberUserId: condition.memberUserId,
    };
  }
  if (condition.mode === 'due_date') {
    return {
      conditionKind: 'card_state',
      field: 'due_date',
      operator: 'has',
    };
  }
  if (condition.mode === 'list_name') {
    return {
      conditionKind: 'card_state',
      field: 'list_name',
      operator: 'contains',
      textValue: condition.text,
    };
  }
  if (condition.mode === 'card_text') {
    return {
      conditionKind: 'card_state',
      field: 'card_name',
      operator: 'contains',
      textValue: condition.text,
    };
  }
  return {
    conditionKind: 'text_match',
    source: 'comment_body',
    operator: 'contains',
    textValue: condition.text,
  };
}

function scheduleSentence(
  t: AutomationPreviewTranslate,
  schedule: AutomationScheduleDraft,
): string {
  if (schedule.frequency === 'daily')
    return t('automationAuthoring.editor.preview.schedule.daily', {
      time: schedule.time,
      timezone: schedule.timezone,
    });
  if (schedule.frequency === 'weekdays') {
    return t('automationAuthoring.editor.preview.schedule.weekdays', {
      time: schedule.time,
      timezone: schedule.timezone,
    });
  }
  if (schedule.frequency === 'weekly') {
    const days =
      schedule.daysOfWeek.length === 0
        ? t('automationAuthoring.editor.preview.fallback.chosenDays')
        : schedule.daysOfWeek.join(', ');
    return t('automationAuthoring.editor.preview.schedule.weekly', {
      days,
      time: schedule.time,
      timezone: schedule.timezone,
    });
  }
  if (schedule.frequency === 'monthly') {
    return t('automationAuthoring.editor.preview.schedule.monthly', {
      day: schedule.dayOfMonth ?? 1,
      time: schedule.time,
      timezone: schedule.timezone,
    });
  }
  return t('automationAuthoring.editor.preview.schedule.yearly', {
    date: schedule.monthDay ?? '01-01',
    time: schedule.time,
    timezone: schedule.timezone,
  });
}

function durationText(t: AutomationPreviewTranslate, minutes: number): string {
  if (minutes % 1440 === 0) {
    const count = minutes / 1440;
    return t('automationAuthoring.editor.preview.duration.days', { count }, count);
  }
  if (minutes % 60 === 0) {
    const count = minutes / 60;
    return t('automationAuthoring.editor.preview.duration.hours', { count }, count);
  }
  return t(
    'automationAuthoring.editor.preview.duration.minutes',
    { count: minutes },
    minutes,
  );
}

function dueDateSentence(t: AutomationPreviewTranslate, offsetMinutes: number): string {
  if (offsetMinutes === 0) return t('automationAuthoring.editor.preview.dueDate.atDue');
  const absolute = Math.abs(offsetMinutes);
  const amount = durationText(t, absolute);
  return offsetMinutes > 0
    ? t('automationAuthoring.editor.preview.dueDate.before', { amount })
    : t('automationAuthoring.editor.preview.dueDate.after', { amount });
}

function actionSentence(
  t: AutomationPreviewTranslate,
  action: DraftAction,
  lists: readonly BoardFaceList[],
  labels: readonly Label[],
): string {
  const list = lists.find((item) => item.id === action.targetListId);
  const label = labels.find((item) => item.id === action.targetLabelId);
  switch (action.actionKind) {
    case 'post_comment':
      return t('automationAuthoring.editor.preview.action.postComment', {
        text: action.text,
      });
    case 'rename_card':
      return t('automationAuthoring.editor.preview.action.renameCard', {
        text: action.text,
      });
    case 'create_card':
      return t('automationAuthoring.editor.preview.action.createCard', {
        text: action.text,
      });
    case 'create_list':
      return t('automationAuthoring.editor.preview.action.createList', {
        text: action.text,
      });
    case 'set_due_date':
      return t(
        'automationAuthoring.editor.preview.action.setDueDate',
        { count: action.days },
        action.days,
      );
    case 'sort_list':
      return t('automationAuthoring.editor.preview.action.sortList', {
        list: list?.title ?? t('automationAuthoring.editor.preview.fallback.list'),
      });
    case 'add_label':
      return t('automationAuthoring.editor.preview.action.addLabel', {
        label:
          label === undefined
            ? t('automationAuthoring.editor.preview.fallback.label')
            : labelName(label),
      });
    case 'remove_label':
      return t('automationAuthoring.editor.preview.action.removeLabel', {
        label:
          label === undefined
            ? t('automationAuthoring.editor.preview.fallback.label')
            : labelName(label),
      });
    case 'delete_card':
      return t('automationAuthoring.editor.preview.action.deleteCard');
    case 'http_request':
      return t('automationAuthoring.editor.preview.action.httpRequest', {
        method: action.httpMethod,
        url:
          action.httpUrl.trim().length === 0
            ? t('automationAuthoring.editor.preview.fallback.requestUrl')
            : action.httpUrl.trim(),
      });
    case 'send_email': {
      const recipientCount =
        parseEmailRecipients(action.emailTo).length +
        parseEmailRecipients(action.emailCc).length;
      const subject =
        action.emailSubject.trim().length === 0
          ? t('automationAuthoring.editor.preview.fallback.emailSubject')
          : action.emailSubject.trim();
      const recipients =
        recipientCount === 0
          ? t('automationAuthoring.editor.preview.fallback.emailRecipients')
          : t(
              'automationAuthoring.editor.preview.action.emailRecipients',
              { count: recipientCount },
              recipientCount,
            );
      return t('automationAuthoring.editor.preview.action.sendEmail', {
        subject,
        recipients,
      });
    }
    default:
      return t(ACTION_LABEL_KEYS[action.actionKind]);
  }
}

function joinPreviewFragments(
  t: AutomationPreviewTranslate,
  fragments: readonly string[],
  key: string,
): string {
  const [first, ...rest] = fragments;
  if (first === undefined) return '';
  return rest.reduce((left, right) => t(key, { left, right }), first);
}

function commandKindPreviewName(
  t: AutomationPreviewTranslate,
  commandKind: AutomationCommandKind,
): string {
  return t(`automationAuthoring.commandKindSingular.${commandKind}`);
}

function triggerSentence(
  t: AutomationPreviewTranslate,
  commandKind: AutomationCommandKind,
  triggerKind: AutomationRuleTriggerKind,
  targetListId: string,
  triggerText: string,
  schedule: AutomationScheduleDraft,
  dueDateOffsetMinutes: number,
  lists: readonly BoardFaceList[],
): string {
  if (commandKind === 'rule') {
    let trigger = t(RULE_TRIGGER_LABEL_KEYS[triggerKind]);
    if (triggerNeedsList(triggerKind)) {
      trigger = t('automationAuthoring.editor.preview.trigger.listTarget', {
        trigger,
        list:
          lists.find((list) => list.id === targetListId)?.title ??
          t('automationAuthoring.editor.preview.fallback.list'),
      });
    }
    if (triggerNeedsText(triggerKind)) {
      trigger = t('automationAuthoring.editor.preview.trigger.matchingText', {
        trigger,
        text: triggerText,
      });
    }
    return trigger;
  }

  if (commandKind === 'calendar_command') {
    return scheduleSentence(t, schedule);
  }

  if (commandKind === 'due_date_command') {
    const trigger = dueDateSentence(t, dueDateOffsetMinutes);
    if (targetListId === '') return trigger;
    return t('automationAuthoring.editor.preview.trigger.dueDateList', {
      trigger,
      list:
        lists.find((list) => list.id === targetListId)?.title ??
        t('automationAuthoring.editor.preview.fallback.list'),
    });
  }

  return t('automationAuthoring.editor.preview.trigger.commandRuns', {
    command: commandKindPreviewName(t, commandKind),
  });
}

function automationPreviewSentence(
  t: AutomationPreviewTranslate,
  commandKind: AutomationCommandKind,
  triggerKind: AutomationRuleTriggerKind,
  targetListId: string,
  triggerText: string,
  conditions: readonly DraftCondition[],
  schedule: AutomationScheduleDraft,
  dueDateOffsetMinutes: number,
  actions: readonly DraftAction[],
  lists: readonly BoardFaceList[],
  labels: readonly Label[],
  members: readonly BoardMember[],
): string {
  const trigger = triggerSentence(
    t,
    commandKind,
    triggerKind,
    targetListId,
    triggerText,
    schedule,
    dueDateOffsetMinutes,
    lists,
  );
  const condition = joinPreviewFragments(
    t,
    conditions.map((item) =>
      conditionText(
        t,
        item.mode,
        labels,
        members,
        item.labelId,
        item.memberUserId,
        item.text,
      ),
    ),
    'automationAuthoring.editor.preview.join.and',
  );
  const actionText = joinPreviewFragments(
    t,
    actions.map((action) => actionSentence(t, action, lists, labels)),
    'automationAuthoring.editor.preview.join.then',
  );
  return condition === ''
    ? t('automationAuthoring.editor.preview.sentence.noCondition', {
        trigger,
        actions: actionText,
      })
    : t('automationAuthoring.editor.preview.sentence.withCondition', {
        trigger,
        condition,
        actions: actionText,
      });
}

function buildAction(
  action: DraftAction,
  index: number,
): CreateAutomationCommandBody['actions'][number] {
  const base = {
    actionKind: action.actionKind,
    position: `${String.fromCharCode(97 + index)}0`,
    targetBoardId: null,
    targetListId: action.targetListId === '' ? null : action.targetListId,
    targetLabelId: action.targetLabelId === '' ? null : action.targetLabelId,
  };

  switch (action.actionKind) {
    case 'post_comment':
      return { ...base, actionKind: 'post_comment', parameters: { body: action.text } };
    case 'rename_card':
      return { ...base, actionKind: 'rename_card', parameters: { title: action.text } };
    case 'create_card':
      return {
        ...base,
        actionKind: 'create_card',
        parameters: { title: action.text, description: '' },
      };
    case 'create_list':
      return { ...base, actionKind: 'create_list', parameters: { title: action.text } };
    case 'set_due_date':
      return {
        ...base,
        actionKind: 'set_due_date',
        parameters: {
          mode: 'relative_days',
          daysFromNow: action.days,
          dueReminderMinutesBefore: null,
        },
      };
    case 'sort_list':
      return {
        ...base,
        actionKind: 'sort_list',
        parameters: { field: 'due_date', direction: 'ascending' },
      };
    case 'add_label':
      return { ...base, actionKind: 'add_label', parameters: {} };
    case 'remove_label':
      return { ...base, actionKind: 'remove_label', parameters: {} };
    case 'delete_card':
      return { ...base, actionKind: 'delete_card', parameters: {} };
    case 'http_request': {
      const { headers } = parseHeaderLines(action.httpHeaders);
      return {
        ...base,
        actionKind: 'http_request',
        targetListId: null,
        targetLabelId: null,
        parameters: {
          method: action.httpMethod,
          url: action.httpUrl.trim(),
          headers,
          body: action.httpMethod === 'GET' ? '' : action.httpBody,
        },
      };
    }
    case 'send_email':
      return {
        ...base,
        actionKind: 'send_email',
        targetListId: null,
        targetLabelId: null,
        parameters: {
          to: parseEmailRecipients(action.emailTo),
          cc: parseEmailRecipients(action.emailCc),
          subject: action.emailSubject.trim(),
          body: action.emailBody,
        },
      };
    default:
      return { ...base, actionKind: action.actionKind, parameters: {} };
  }
}

/**
 * ⚠ THE VALIDATION CHANNEL CARRIES A KEY AND ITS SLOT VALUES, NOT A SENTENCE.
 * `validation` is `useState`, and a state holding resolved copy keeps
 * announcing the PREVIOUS language after a switch. Two of the twelve messages
 * carry a slot, so a bare key is not enough and the pair travels together.
 */
export interface AutomationMessage {
  readonly key: string;
  readonly values?: Record<string, string | number>;
}

export type AutomationValidation =
  | AutomationMessage
  /*
    ⚠ The one arm that is NOT a key: Zod's own message is composed by
    `packages/shared` and arrives as a finished sentence. It stays literal
    (§7); everything beside it is a catalogue key.
  */
  | { readonly serverText: string };

const ACTION_BLANK_REQUIRED: AutomationMessage = {
  key: 'automationAuthoring.editor.validation.actionBlankRequired',
};

function validateAction(action: DraftAction): AutomationValidation | null {
  if (
    ['post_comment', 'rename_card', 'create_card', 'create_list'].includes(
      action.actionKind,
    )
  ) {
    return action.text.trim().length > 0 ? null : ACTION_BLANK_REQUIRED;
  }
  if (['add_label', 'remove_label'].includes(action.actionKind))
    return action.targetLabelId !== '' ? null : ACTION_BLANK_REQUIRED;
  if (action.actionKind === 'sort_list')
    return action.targetListId !== '' ? null : ACTION_BLANK_REQUIRED;
  if (action.actionKind === 'http_request') {
    if (action.httpUrl.trim().length === 0) {
      return { key: 'automationAuthoring.editor.http.urlRequired' };
    }
    const parsedHeaders = parseHeaderLines(action.httpHeaders);
    if (parsedHeaders.error !== null) return { key: parsedHeaders.error };
    if (parsedHeaders.maskedSecretHeaderName !== null) {
      return {
        key: 'automationAuthoring.editor.http.maskedHeaderError',
        values: { header: parsedHeaders.maskedSecretHeaderName },
      };
    }
  }
  if (action.actionKind === 'send_email') {
    const to = parseEmailRecipients(action.emailTo);
    const cc = parseEmailRecipients(action.emailCc);
    if (to.length === 0) {
      return { key: 'automationAuthoring.editor.email.recipientRequired' };
    }
    if (to.length + cc.length > AUTOMATION_EMAIL_RECIPIENT_LIMIT) {
      return {
        key: 'automationAuthoring.editor.email.recipientLimit',
        values: { limit: AUTOMATION_EMAIL_RECIPIENT_LIMIT },
      };
    }
    if (hasMalformedEmailRecipient(to) || hasMalformedEmailRecipient(cc)) {
      return { key: 'automationAuthoring.editor.email.recipientInvalid' };
    }
    if (action.emailSubject.trim().length === 0) {
      return { key: 'automationAuthoring.editor.email.subjectRequired' };
    }
    if (action.emailBody.trim().length === 0) {
      return { key: 'automationAuthoring.editor.email.bodyRequired' };
    }
  }
  return null;
}

/** ⚠ Returns a KEY and the value its slot needs — see `validateAction` above. */
function ruleLoopSafety(commandKind: AutomationCommandKind): AutomationMessage | null {
  if (commandKind !== 'rule') return null;
  return {
    key: 'automationAuthoring.editor.loopSafetyBody',
    values: { limit: AUTOMATION_CHAIN_DEPTH_LIMIT },
  };
}

export function AutomationEditor({
  currentUserId = null,
  lists,
  labels,
  members,
  commandKind,
  command,
  busy = false,
  error,
  activeButtonLimitNotice,
  onSubmit,
  onCancel,
}: AutomationEditorProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const ids = useId();
  const [name, setName] = useState(command?.name ?? '');
  const [triggerKind, setTriggerKind] = useState<AutomationRuleTriggerKind>(
    command?.trigger?.triggerKind &&
      (AUTOMATION_RULE_TRIGGER_KINDS as readonly string[]).includes(
        command.trigger.triggerKind,
      )
      ? (command.trigger.triggerKind as AutomationRuleTriggerKind)
      : 'card_added_to_list',
  );
  const [targetListId, setTargetListId] = useState(
    initialTriggerListId(commandKind, command, lists),
  );
  const [triggerText, setTriggerText] = useState(command?.trigger?.textMatch ?? '/done');
  const actingUserId = command?.actingUserId ?? currentUserId ?? '';
  const [newConditionMode, setNewConditionMode] = useState<ConditionMode>('actor_not_me');
  const [conditions, setConditions] = useState<DraftCondition[]>(
    command?.trigger?.conditions
      .map((condition, index) =>
        conditionFromCommandCondition(condition, index, labels, members, actingUserId),
      )
      .filter((condition): condition is DraftCondition => condition !== null) ?? [],
  );
  const [schedule, setSchedule] = useState<AutomationScheduleDraft>(() =>
    scheduleDraftFromCommand(command),
  );
  const [dueDateOffsetMinutes, setDueDateOffsetMinutes] = useState(
    command?.dueDateOffsetMinutes ?? 1440,
  );
  const [newActionKind, setNewActionKind] =
    useState<AutomationActionKind>('post_comment');
  const [validation, setValidation] = useState<AutomationValidation | null>(null);
  const [actions, setActions] = useState<DraftAction[]>(
    command?.actions.length
      ? command.actions.map(actionFromCommandAction)
      : [newDraftAction('post_comment', lists, labels)],
  );
  const actionLimitReached = actions.length >= AUTOMATION_ACTION_LIMIT;
  const loopSafety = ruleLoopSafety(commandKind);
  /* `BE-68.11`. `null` for the 24 rule kinds that fire, and for every
     non-rule command — a calendar or due-date command constructs its
     trigger row directly and never reaches the picker. */
  const unsupportedTrigger =
    commandKind === 'rule' ? unsupportedAutomationTriggerReason(triggerKind) : null;
  const activeButtonLimitKey = ACTIVE_BUTTON_LIMIT_KEYS[commandKind];
  const activeButtonLimitBody =
    activeButtonLimitNotice ??
    (activeButtonLimitKey === null
      ? null
      : t(activeButtonLimitKey, { limit: AUTOMATION_ACTIVE_BUTTON_LIMIT }));

  useEffect(() => {
    if (command !== null && command !== undefined) return;
    if (actions.length === 0) setActions([newDraftAction('post_comment', lists, labels)]);
  }, [actions.length, command, labels, lists]);

  const sentence = useMemo(
    () =>
      automationPreviewSentence(
        t,
        commandKind,
        triggerKind,
        targetListId,
        triggerText,
        conditions,
        schedule,
        dueDateOffsetMinutes,
        actions,
        lists,
        labels,
        members,
      ),
    [
      actions,
      commandKind,
      conditions,
      dueDateOffsetMinutes,
      labels,
      lists,
      members,
      schedule,
      t,
      targetListId,
      triggerKind,
      triggerText,
    ],
  );
  const plainText = useMemo(
    () =>
      automationPreviewSentence(
        sourcePreviewMessage,
        commandKind,
        triggerKind,
        targetListId,
        triggerText,
        conditions,
        schedule,
        dueDateOffsetMinutes,
        actions,
        lists,
        labels,
        members,
      ),
    [
      actions,
      commandKind,
      conditions,
      dueDateOffsetMinutes,
      labels,
      lists,
      members,
      schedule,
      targetListId,
      triggerKind,
      triggerText,
    ],
  );

  function updateAction(key: string, patch: Partial<DraftAction>): void {
    setActions((previous) =>
      previous.map((action) => (action.key === key ? { ...action, ...patch } : action)),
    );
  }

  function moveAction(index: number, direction: -1 | 1): void {
    setActions((previous) => {
      const next = [...previous];
      const [item] = next.splice(index, 1);
      if (item === undefined) return previous;
      next.splice(index + direction, 0, item);
      return next;
    });
  }

  function removeAction(key: string): void {
    setActions((previous) => previous.filter((action) => action.key !== key));
  }

  function updateCondition(key: string, patch: Partial<DraftCondition>): void {
    setConditions((previous) =>
      previous.map((condition) =>
        condition.key === key ? { ...condition, ...patch } : condition,
      ),
    );
  }

  function addCondition(): void {
    setConditions((previous) => [
      ...previous,
      newDraftCondition(newConditionMode, labels, members),
    ]);
  }

  function removeCondition(key: string): void {
    setConditions((previous) => previous.filter((condition) => condition.key !== key));
  }

  function addAction(): void {
    if (actionLimitReached) {
      setValidation({
        key: 'automationAuthoring.editor.action.limitMessage',
        values: { limit: AUTOMATION_ACTION_LIMIT },
      });
      return;
    }
    setActions((previous) => [...previous, newDraftAction(newActionKind, lists, labels)]);
  }

  function appendVariable(variable: string): void {
    setActions((previous) => {
      const [first, ...rest] = previous;
      if (first === undefined) return previous;
      if (first.actionKind === 'send_email') {
        return [{ ...first, emailBody: `${first.emailBody}${variable}` }, ...rest];
      }
      return [{ ...first, text: `${first.text}${variable}` }, ...rest];
    });
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setValidation({ key: 'automationAuthoring.editor.validation.nameRequired' });
      return;
    }
    if (commandKind === 'rule' && triggerNeedsList(triggerKind) && targetListId === '') {
      setValidation({ key: 'automationAuthoring.editor.validation.triggerListRequired' });
      return;
    }
    if (
      commandKind === 'rule' &&
      triggerNeedsText(triggerKind) &&
      triggerText.trim().length === 0
    ) {
      setValidation({ key: 'automationAuthoring.editor.validation.triggerTextRequired' });
      return;
    }
    if (commandKind === 'calendar_command') {
      if (schedule.frequency === 'weekly' && schedule.daysOfWeek.length === 0) {
        setValidation({ key: 'automationAuthoring.editor.validation.weekdayRequired' });
        return;
      }
      if (schedule.frequency === 'monthly' && schedule.dayOfMonth === null) {
        setValidation({
          key: 'automationAuthoring.editor.validation.dayOfMonthRequired',
        });
        return;
      }
      if (schedule.frequency === 'yearly' && schedule.monthDay === null) {
        setValidation({
          key: 'automationAuthoring.editor.validation.yearlyDateRequired',
        });
        return;
      }
    }
    for (const condition of conditions) {
      if (
        (condition.mode === 'label' || condition.mode === 'without_label') &&
        condition.labelId === ''
      ) {
        setValidation({
          key: 'automationAuthoring.editor.validation.conditionLabelRequired',
        });
        return;
      }
      if (
        (condition.mode === 'actor_specific_member' ||
          condition.mode === 'assigned_to_member') &&
        condition.memberUserId === ''
      ) {
        setValidation({
          key: 'automationAuthoring.editor.validation.conditionMemberRequired',
        });
        return;
      }
      if (condition.mode === 'assigned_to_me' && actingUserId === '') {
        setValidation({
          key: 'automationAuthoring.editor.validation.actingMemberRequired',
        });
        return;
      }
      if (
        (condition.mode === 'list_name' ||
          condition.mode === 'card_text' ||
          condition.mode === 'comment_text') &&
        condition.text.trim().length === 0
      ) {
        setValidation({
          key: 'automationAuthoring.editor.validation.conditionTextRequired',
        });
        return;
      }
    }
    const actionValidation = actions
      .map(validateAction)
      .find((message) => message !== null);
    if (actionValidation !== undefined) {
      setValidation(actionValidation);
      return;
    }

    const builtActions = actions.map(buildAction);
    const base = {
      commandKind,
      name: trimmedName,
      icon: null,
      plainText,
      visibility: 'shared',
      scopeKind: 'single_board',
      actions: builtActions,
    };

    const builtConditions = conditions.map((condition) =>
      buildCondition(condition, actingUserId),
    );

    const body =
      commandKind === 'rule'
        ? {
            ...base,
            commandKind: 'rule' as const,
            trigger: {
              triggerKind,
              targetListId: triggerNeedsList(triggerKind) ? targetListId : null,
              targetLabelId: null,
              textMatch: triggerNeedsText(triggerKind) ? triggerText : null,
              conditions: builtConditions,
            },
          }
        : commandKind === 'calendar_command'
          ? {
              ...base,
              commandKind: 'calendar_command' as const,
              trigger: { triggerKind: 'schedule_reached' as const, conditions: [] },
              schedule: {
                frequency: schedule.frequency,
                daysOfWeek: schedule.frequency === 'weekly' ? schedule.daysOfWeek : [],
                dayOfMonth: schedule.frequency === 'monthly' ? schedule.dayOfMonth : null,
                monthDay: schedule.frequency === 'yearly' ? schedule.monthDay : null,
                time: schedule.time,
                timezone: schedule.timezone,
              },
            }
          : commandKind === 'due_date_command'
            ? {
                ...base,
                commandKind: 'due_date_command' as const,
                trigger: {
                  triggerKind: 'due_date_offset_reached' as const,
                  targetListId: targetListId === '' ? null : targetListId,
                  targetLabelId: null,
                  textMatch: null,
                  conditions: [],
                },
                dueDateOffsetMinutes,
              }
            : base;

    const parsed = createAutomationCommandBodySchema.safeParse(body);
    if (!parsed.success) {
      /*
        ⚠ Zod's own message is composed by `packages/shared` and is already a
        finished sentence, so it is passed through as `text`. Only the fallback
        beside it is a catalogue key — hence the discriminated pair.
      */
      const zodMessage = parsed.error.issues[0]?.message;
      setValidation(
        zodMessage === undefined
          ? { key: 'automationAuthoring.editor.validation.fallback' }
          : { serverText: zodMessage },
      );
      return;
    }
    setValidation(null);
    onSubmit(parsed.data);
  }

  /**
   * ⚠ RESOLVED AT RENDER, NOT AT `setValidation`. The state holds a KEY, so a
   * language switch with a validation message on screen re-announces in the
   * NEW language rather than repeating the previous one.
   */
  const validationMessage =
    validation === null
      ? null
      : 'serverText' in validation
        ? validation.serverText
        : t(validation.key, validation.values);

  const unnamedLabel = t('automationAuthoring.editor.unnamedLabel');

  const actionOptions = AUTOMATION_ACTION_KINDS.map((actionKind) => (
    <option key={actionKind} value={actionKind}>
      {RUNTIME_SUPPORTED_ACTIONS.has(actionKind)
        ? t(ACTION_LABEL_KEYS[actionKind])
        : t('automationAuthoring.editor.action.optionStoredOnly', {
            action: t(ACTION_LABEL_KEYS[actionKind]),
          })}
    </option>
  ));

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <div className="sticky top-0 z-10 flex flex-col gap-2 rounded-card border border-border-strong bg-surface-raised p-3">
        <TextField
          label={t('automationAuthoring.editor.nameLabel')}
          value={name}
          disabled={busy}
          aria-invalid={validation !== null && name.trim().length === 0}
          onChange={(event) => setName(event.currentTarget.value)}
        />
        <div
          role="status"
          aria-label={t('automationAuthoring.editor.sentenceLabel')}
          className="max-h-40 overflow-auto rounded-card bg-surface px-3 py-2 text-card text-ink"
        >
          {sentence}
        </div>
        {members.length === 0 ? (
          <p className="text-xs text-ink-muted">
            {t('automationAuthoring.editor.membersHint')}
          </p>
        ) : null}
        {activeButtonLimitBody === null ? null : (
          <Notice tone="warn" body={activeButtonLimitBody} />
        )}
        {loopSafety === null ? null : (
          <Notice
            tone="info"
            title={t('automationAuthoring.editor.loopSafetyTitle')}
            body={t(loopSafety.key, loopSafety.values)}
          />
        )}
      </div>

      {commandKind === 'rule' ? (
        <fieldset className="flex flex-col gap-2 rounded-card border border-border-strong bg-surface-raised p-3">
          <legend className="px-1 text-sm font-semibold text-ink">
            {t('automationAuthoring.editor.trigger.legend')}
          </legend>
          <label
            className="flex flex-col gap-1 text-sm font-medium text-ink"
            htmlFor={`${ids}-trigger`}
          >
            {t('automationAuthoring.editor.trigger.phraseLabel')}
            <select
              id={`${ids}-trigger`}
              className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
              value={triggerKind}
              disabled={busy}
              onChange={(event) =>
                setTriggerKind(event.currentTarget.value as AutomationRuleTriggerKind)
              }
            >
              {AUTOMATION_RULE_TRIGGER_KINDS.map((kind) => {
                /* ⚠ ANNOTATED, NEVER `disabled`. The editor hydrates
                   `triggerKind` from a SAVED command, so an already-stored
                   `card_voted_on` rule opens with this option selected;
                   disabling it would give the admin a `<select>` whose current
                   value cannot be read back or re-saved, with nothing red. */
                const label = t(RULE_TRIGGER_LABEL_KEYS[kind]);
                return (
                  <option key={kind} value={kind}>
                    {unsupportedAutomationTriggerReason(kind) === null
                      ? label
                      : `${label} ${t(
                          'automationAuthoring.editor.trigger.unsupportedSuffix',
                        )}`}
                  </option>
                );
              })}
            </select>
          </label>
          {unsupportedTrigger === null ? null : (
            <Notice
              tone="warn"
              title={t('automationAuthoring.editor.trigger.unsupportedTitle')}
              body={t(UNSUPPORTED_TRIGGER_REASON_KEYS[unsupportedTrigger])}
            />
          )}
          {triggerNeedsList(triggerKind) ? (
            <label
              className="flex flex-col gap-1 text-sm font-medium text-ink"
              htmlFor={`${ids}-trigger-list`}
            >
              {t('automationAuthoring.editor.trigger.listLabel')}
              <select
                id={`${ids}-trigger-list`}
                className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                value={targetListId}
                disabled={busy}
                aria-invalid={validation !== null && targetListId === ''}
                onChange={(event) => setTargetListId(event.currentTarget.value)}
              >
                <option value="">
                  {t('automationAuthoring.editor.trigger.listPlaceholder')}
                </option>
                {lists.map((list) => (
                  <option key={list.id} value={list.id}>
                    {list.title}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {triggerNeedsText(triggerKind) ? (
            <TextField
              label={t('automationAuthoring.editor.trigger.textLabel')}
              value={triggerText}
              disabled={busy}
              aria-invalid={
                validation !== null &&
                triggerNeedsText(triggerKind) &&
                triggerText.trim().length === 0
              }
              onChange={(event) => setTriggerText(event.currentTarget.value)}
            />
          ) : null}
        </fieldset>
      ) : null}

      {commandKind === 'calendar_command' || commandKind === 'due_date_command' ? (
        <AutomationScheduleFields
          mode={commandKind}
          schedule={schedule}
          dueDateOffsetMinutes={dueDateOffsetMinutes}
          disabled={busy}
          onScheduleChange={setSchedule}
          onDueDateOffsetMinutesChange={setDueDateOffsetMinutes}
        />
      ) : null}

      {commandKind === 'due_date_command' ? (
        <fieldset className="flex flex-col gap-2 rounded-card border border-border-strong bg-surface-raised p-3">
          <legend className="px-1 text-sm font-semibold text-ink">
            {t('automationAuthoring.editor.cardScope.legend')}
          </legend>
          <label
            className="flex flex-col gap-1 text-sm font-medium text-ink"
            htmlFor={`${ids}-due-list`}
          >
            {t('automationAuthoring.editor.cardScope.listLabel')}
            <select
              id={`${ids}-due-list`}
              className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
              value={targetListId}
              disabled={busy}
              onChange={(event) => setTargetListId(event.currentTarget.value)}
            >
              <option value="">
                {t('automationAuthoring.editor.cardScope.anyList')}
              </option>
              {lists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.title}
                </option>
              ))}
            </select>
          </label>
        </fieldset>
      ) : null}

      <fieldset className="flex flex-col gap-2 rounded-card border border-border-strong bg-surface-raised p-3">
        <legend className="px-1 text-sm font-semibold text-ink">
          {t('automationAuthoring.editor.condition.legend')}
        </legend>
        <div className="flex flex-wrap gap-2">
          <label
            className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium text-ink"
            htmlFor={`${ids}-condition`}
          >
            {t('automationAuthoring.editor.condition.addLabel')}
            <select
              id={`${ids}-condition`}
              className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
              value={newConditionMode}
              disabled={busy}
              onChange={(event) =>
                setNewConditionMode(event.currentTarget.value as ConditionMode)
              }
            >
              {/*
                ⚠ `ConditionMode` is a FRONTEND union, not a wire enum — the
                wire shape is built by `buildCondition`. These eleven texts are
                pure chrome and route; the numbered summary rows below them
                use the SAME preview composer as the live sentence, while submit
                replays that composer through the source catalogue for the
                stored `plainText`.
              */}
              <option value="actor_not_me">
                {t('automationAuthoring.editor.conditionOption.actor_not_me')}
              </option>
              <option value="actor_me">
                {t('automationAuthoring.editor.conditionOption.actor_me')}
              </option>
              <option value="actor_specific_member">
                {t('automationAuthoring.editor.conditionOption.actor_specific_member')}
              </option>
              <option value="label">
                {t('automationAuthoring.editor.conditionOption.label')}
              </option>
              <option value="without_label">
                {t('automationAuthoring.editor.conditionOption.without_label')}
              </option>
              <option value="assigned_to_me">
                {t('automationAuthoring.editor.conditionOption.assigned_to_me')}
              </option>
              <option value="assigned_to_member">
                {t('automationAuthoring.editor.conditionOption.assigned_to_member')}
              </option>
              <option value="due_date">
                {t('automationAuthoring.editor.conditionOption.due_date')}
              </option>
              <option value="list_name">
                {t('automationAuthoring.editor.conditionOption.list_name')}
              </option>
              <option value="card_text">
                {t('automationAuthoring.editor.conditionOption.card_text')}
              </option>
              <option value="comment_text">
                {t('automationAuthoring.editor.conditionOption.comment_text')}
              </option>
            </select>
          </label>
          <Button
            type="button"
            className="self-end"
            onClick={addCondition}
            disabled={busy}
          >
            {t('automationAuthoring.editor.condition.addButton')}
          </Button>
        </div>
        {conditions.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {t('automationAuthoring.editor.condition.empty')}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {conditions.map((condition, index) => (
              <div
                key={condition.key}
                className="flex flex-col gap-2 rounded-card border border-border bg-surface p-2"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
                    {index + 1}.{' '}
                    {conditionText(
                      t,
                      condition.mode,
                      labels,
                      members,
                      condition.labelId,
                      condition.memberUserId,
                      condition.text,
                    )}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="quiet"
                    disabled={busy}
                    onClick={() => removeCondition(condition.key)}
                  >
                    {t('automationAuthoring.editor.condition.remove')}
                  </Button>
                </div>
                {condition.mode === 'label' || condition.mode === 'without_label' ? (
                  <label
                    className="flex flex-col gap-1 text-sm font-medium text-ink"
                    htmlFor={`${ids}-${condition.key}-condition-label`}
                  >
                    {t('automationAuthoring.editor.condition.labelLabel', {
                      index: index + 1,
                    })}
                    <select
                      id={`${ids}-${condition.key}-condition-label`}
                      className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                      value={condition.labelId}
                      disabled={busy}
                      aria-invalid={validation !== null && condition.labelId === ''}
                      onChange={(event) =>
                        updateCondition(condition.key, {
                          labelId: event.currentTarget.value,
                        })
                      }
                    >
                      <option value="">
                        {t('automationAuthoring.editor.condition.labelPlaceholder')}
                      </option>
                      {labels.map((label) => (
                        <option key={label.id} value={label.id}>
                          {labelOptionName(label, unnamedLabel)}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {condition.mode === 'actor_specific_member' ||
                condition.mode === 'assigned_to_member' ? (
                  <label
                    className="flex flex-col gap-1 text-sm font-medium text-ink"
                    htmlFor={`${ids}-${condition.key}-condition-member`}
                  >
                    {t('automationAuthoring.editor.condition.memberLabel', {
                      index: index + 1,
                    })}
                    <select
                      id={`${ids}-${condition.key}-condition-member`}
                      className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                      value={condition.memberUserId}
                      disabled={busy}
                      aria-invalid={validation !== null && condition.memberUserId === ''}
                      onChange={(event) =>
                        updateCondition(condition.key, {
                          memberUserId: event.currentTarget.value,
                        })
                      }
                    >
                      <option value="">
                        {t('automationAuthoring.editor.condition.memberPlaceholder')}
                      </option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {condition.mode === 'list_name' ||
                condition.mode === 'card_text' ||
                condition.mode === 'comment_text' ? (
                  <TextField
                    label={t('automationAuthoring.editor.condition.textLabel', {
                      index: index + 1,
                    })}
                    value={condition.text}
                    disabled={busy}
                    aria-invalid={
                      validation !== null && condition.text.trim().length === 0
                    }
                    onChange={(event) =>
                      updateCondition(condition.key, {
                        text: event.currentTarget.value,
                      })
                    }
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </fieldset>

      <fieldset className="flex flex-col gap-3 rounded-card border border-border-strong bg-surface-raised p-3">
        <legend className="px-1 text-sm font-semibold text-ink">
          {t('automationAuthoring.editor.action.legend')}
        </legend>
        <div className="flex flex-wrap gap-2">
          <label
            className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium text-ink"
            htmlFor={`${ids}-action-kind`}
          >
            {t('automationAuthoring.editor.action.addLabel')}
            <select
              id={`${ids}-action-kind`}
              className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
              value={newActionKind}
              disabled={busy}
              onChange={(event) =>
                setNewActionKind(event.currentTarget.value as AutomationActionKind)
              }
            >
              {actionOptions}
            </select>
          </label>
          <Button
            type="button"
            className="self-end"
            onClick={addAction}
            disabled={busy || actionLimitReached}
            disabledReason={
              actionLimitReached
                ? t('automationAuthoring.editor.action.limitMessage', {
                    limit: AUTOMATION_ACTION_LIMIT,
                  })
                : undefined
            }
          >
            {t('automationAuthoring.editor.action.addButton')}
          </Button>
        </div>
        {actionLimitReached ? (
          <Notice
            tone="warn"
            body={t('automationAuthoring.editor.action.limitNotice', {
              limit: AUTOMATION_ACTION_LIMIT,
            })}
          />
        ) : null}

        <div className="flex flex-col gap-2">
          {actions.map((action, index) => (
            <div
              key={action.key}
              className={cn(
                'flex flex-col gap-2 rounded-card border border-border bg-surface p-2',
                action.actionKind === 'delete_card' && 'border-bad',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-sm font-semibold text-ink">
                  {index + 1}. {t(ACTION_LABEL_KEYS[action.actionKind])}
                </span>
                {!RUNTIME_SUPPORTED_ACTIONS.has(action.actionKind) ? (
                  <span className="rounded-card bg-border px-2 py-0.5 text-xs text-ink-muted">
                    {t('automationAuthoring.editor.action.storedOnlyChip')}
                  </span>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  disabled={index === 0 || busy}
                  onClick={() => moveAction(index, -1)}
                >
                  {t('automationAuthoring.editor.action.moveUp')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  disabled={index === actions.length - 1 || busy}
                  onClick={() => moveAction(index, 1)}
                >
                  {t('automationAuthoring.editor.action.moveDown')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  disabled={actions.length === 1 || busy}
                  onClick={() => removeAction(action.key)}
                >
                  {t('automationAuthoring.editor.action.remove')}
                </Button>
              </div>

              {['post_comment', 'rename_card', 'create_card', 'create_list'].includes(
                action.actionKind,
              ) ? (
                <TextField
                  label={t('automationAuthoring.editor.action.textLabel', {
                    action: t(ACTION_LABEL_KEYS[action.actionKind]),
                  })}
                  value={action.text}
                  disabled={busy}
                  aria-invalid={validation !== null && action.text.trim().length === 0}
                  onChange={(event) =>
                    updateAction(action.key, { text: event.currentTarget.value })
                  }
                />
              ) : null}
              {action.actionKind === 'set_due_date' ? (
                <TextField
                  label={t('automationAuthoring.editor.action.daysLabel')}
                  type="number"
                  value={action.days}
                  disabled={busy}
                  onChange={(event) =>
                    updateAction(action.key, { days: Number(event.currentTarget.value) })
                  }
                />
              ) : null}
              {['add_label', 'remove_label'].includes(action.actionKind) ? (
                <label
                  className="flex flex-col gap-1 text-sm font-medium text-ink"
                  htmlFor={`${ids}-${action.key}-label`}
                >
                  {t('automationAuthoring.editor.action.labelLabel')}
                  <select
                    id={`${ids}-${action.key}-label`}
                    className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                    value={action.targetLabelId}
                    disabled={busy}
                    aria-invalid={validation !== null && action.targetLabelId === ''}
                    onChange={(event) =>
                      updateAction(action.key, {
                        targetLabelId: event.currentTarget.value,
                      })
                    }
                  >
                    <option value="">
                      {t('automationAuthoring.editor.action.labelPlaceholder')}
                    </option>
                    {labels.map((label) => (
                      <option key={label.id} value={label.id}>
                        {labelOptionName(label, unnamedLabel)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {action.actionKind === 'sort_list' ? (
                <label
                  className="flex flex-col gap-1 text-sm font-medium text-ink"
                  htmlFor={`${ids}-${action.key}-list`}
                >
                  {t('automationAuthoring.editor.action.listLabel')}
                  <select
                    id={`${ids}-${action.key}-list`}
                    className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                    value={action.targetListId}
                    disabled={busy}
                    aria-invalid={validation !== null && action.targetListId === ''}
                    onChange={(event) =>
                      updateAction(action.key, {
                        targetListId: event.currentTarget.value,
                      })
                    }
                  >
                    <option value="">
                      {t('automationAuthoring.editor.action.listPlaceholder')}
                    </option>
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.title}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {action.actionKind === 'http_request' ? (
                <div className="flex flex-col gap-2">
                  <Notice
                    tone="info"
                    /*
                      ⚠ THE SLOT IS FILLED WITH ITSELF. `interfaceMessage`'s
                      slot regex is `\{([A-Za-z0-9_]+)\}`, so the declared
                      `{httpresponse}` in this sentence THROWS into the render
                      unless a value is supplied. Supplying the token as its own
                      value keeps the output byte-identical and keeps the
                      sentence one string for a translator. (`{httpresponse.property}`
                      does not match — the dot saves it.) This is the only
                      token-bearing string in the slice that routes; the other
                      twenty-eight are left literal because they are values the
                      form POSTS, not prose.
                    */
                    body={t('automationAuthoring.editor.http.notice', {
                      httpresponse: '{httpresponse}',
                    })}
                  />
                  <label
                    className="flex flex-col gap-1 text-sm font-medium text-ink"
                    htmlFor={`${ids}-${action.key}-http-method`}
                  >
                    {t('automationAuthoring.editor.http.methodLabel')}
                    <select
                      id={`${ids}-${action.key}-http-method`}
                      className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                      value={action.httpMethod}
                      disabled={busy}
                      onChange={(event) =>
                        updateAction(action.key, {
                          httpMethod: event.currentTarget.value as AutomationHttpMethod,
                        })
                      }
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                    </select>
                  </label>
                  <TextField
                    label={t('automationAuthoring.editor.http.urlLabel')}
                    value={action.httpUrl}
                    disabled={busy}
                    aria-invalid={
                      validation !== null && action.httpUrl.trim().length === 0
                    }
                    placeholder={t('automationAuthoring.editor.http.urlPlaceholder')}
                    onChange={(event) =>
                      updateAction(action.key, { httpUrl: event.currentTarget.value })
                    }
                  />
                  <TextField
                    label={t('automationAuthoring.editor.http.headersLabel')}
                    value={action.httpHeaders}
                    disabled={busy}
                    multiline
                    rows={3}
                    aria-invalid={
                      validation !== null &&
                      action.actionKind === 'http_request' &&
                      (parseHeaderLines(action.httpHeaders).error !== null ||
                        parseHeaderLines(action.httpHeaders).maskedSecretHeaderName !==
                          null)
                    }
                    placeholder={t('automationAuthoring.editor.http.headersPlaceholder')}
                    onChange={(event) =>
                      updateAction(action.key, {
                        httpHeaders: event.currentTarget.value,
                      })
                    }
                  />
                  <TextField
                    label={t('automationAuthoring.editor.http.bodyLabel')}
                    value={action.httpBody}
                    disabled={busy || action.httpMethod === 'GET'}
                    multiline
                    rows={4}
                    placeholder={t('automationAuthoring.editor.http.bodyPlaceholder', {
                      cardlink: '{cardlink}',
                    })}
                    onChange={(event) =>
                      updateAction(action.key, { httpBody: event.currentTarget.value })
                    }
                  />
                </div>
              ) : null}
              {action.actionKind === 'send_email' ? (
                <div className="flex flex-col gap-2">
                  <TextField
                    label={t('automationAuthoring.editor.email.toLabel')}
                    value={action.emailTo}
                    disabled={busy}
                    multiline
                    rows={3}
                    aria-invalid={
                      validation !== null &&
                      (parseEmailRecipients(action.emailTo).length === 0 ||
                        parseEmailRecipients(action.emailTo).length +
                          parseEmailRecipients(action.emailCc).length >
                          AUTOMATION_EMAIL_RECIPIENT_LIMIT ||
                        hasMalformedEmailRecipient(parseEmailRecipients(action.emailTo)))
                    }
                    placeholder={t('automationAuthoring.editor.email.toPlaceholder')}
                    hint={t('automationAuthoring.editor.email.toHint')}
                    onChange={(event) =>
                      updateAction(action.key, {
                        emailTo: event.currentTarget.value,
                      })
                    }
                  />
                  <TextField
                    label={t('automationAuthoring.editor.email.ccLabel')}
                    value={action.emailCc}
                    disabled={busy}
                    multiline
                    rows={2}
                    aria-invalid={
                      validation !== null &&
                      hasMalformedEmailRecipient(parseEmailRecipients(action.emailCc))
                    }
                    placeholder={t('automationAuthoring.editor.email.ccPlaceholder')}
                    onChange={(event) =>
                      updateAction(action.key, {
                        emailCc: event.currentTarget.value,
                      })
                    }
                  />
                  <TextField
                    label={t('automationAuthoring.editor.email.subjectLabel')}
                    value={action.emailSubject}
                    disabled={busy}
                    aria-invalid={
                      validation !== null && action.emailSubject.trim().length === 0
                    }
                    placeholder={t(
                      'automationAuthoring.editor.email.subjectPlaceholder',
                      { cardname: '{cardname}' },
                    )}
                    onChange={(event) =>
                      updateAction(action.key, {
                        emailSubject: event.currentTarget.value,
                      })
                    }
                  />
                  <TextField
                    label={t('automationAuthoring.editor.email.bodyLabel')}
                    value={action.emailBody}
                    disabled={busy}
                    multiline
                    rows={4}
                    aria-invalid={
                      validation !== null && action.emailBody.trim().length === 0
                    }
                    placeholder={t('automationAuthoring.editor.email.bodyPlaceholder', {
                      cardname: '{cardname}',
                      cardlink: '{cardlink}',
                    })}
                    onChange={(event) =>
                      updateAction(action.key, {
                        emailBody: event.currentTarget.value,
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>

        <div
          className="flex flex-wrap gap-2"
          aria-label={t('automationAuthoring.editor.variablesLabel')}
        >
          {AUTOMATION_TEXT_VARIABLES.map((variable) => (
            <Button
              key={variable}
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => appendVariable(variable)}
            >
              {variable}
            </Button>
          ))}
        </div>
      </fieldset>

      {validationMessage === null ? null : <Notice tone="bad" body={validationMessage} />}
      {error !== undefined ? <Notice tone="bad" body={error} /> : null}

      <div className="flex flex-wrap gap-2 border-t border-border bg-surface py-3">
        <Button type="submit" variant="primary" busy={busy}>
          {t('automationAuthoring.editor.save')}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          {t('automationAuthoring.editor.cancel')}
        </Button>
      </div>
    </form>
  );
}
